import React, { useEffect, useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import {
  Feature,
  FeatureStatus,
  Project,
  ProjectStatus,
  SummaryConfig,
  SummaryConfigInput,
  SummaryGapFill,
  SummaryKind,
  SummaryTier,
  createSummaryConfig,
  getFeatures,
  getProjects,
  updateSummaryConfig,
} from '../../api/client'
import { boolLabels, isBoolSub, type BoolDayRule } from '../../utils/boolFeature'

/**
 * SummaryConfigEditor — the CRUD modal for activity-summary configs. One modal
 * covering the Phase 1 kinds, reachable from the feature, project, and dashboard
 * pages.
 *
 * Source is chosen IN the editor (a Feature / Project-tag toggle), not dictated
 * by the page (Stage 17). The `surface` prop only sets defaults: which source
 * type opens selected, and whether the dashboard pin is implicit (dashboard) or
 * an opt-in checkbox (feature/project). A summary shows on its source's
 * feature/project page via source membership; `pin_surfaces` is a dashboard-only
 * opt-in.
 *
 * Kind → params mapping (only the row relevant to the selected kind shows):
 *   single_feature_stats  → top_n (top-N categories)
 *   outliers              → top_n (top/bottom N)
 *   stratified_single     → strat_feature_id (paired-feature stratification key)
 *   period_over_period    → same_day_agg (sum | mean of same-day check-ins),
 *                           progress_direction (higher | lower | none — which
 *                           way the chart paints green)
 *   streaks               → cadence (daily | weekly | monthly — the bucket a
 *                           streak counts in; the streak stays alive with at
 *                           least one check-in per day / week / month)
 *   logging_rate          → cadence (daily | weekly | monthly — the bucket the
 *                           % of logged periods is computed over)
 *   streaks / logging_rate on a yes/no source feature
 *                         → count (yes | no — which answer's days count; shown
 *                           in the feature's own labels, always persisted)
 *   streaks / logging_rate / single_feature_stats on a yes/no source feature
 *                         → day_rule (any | all — whether one checkin giving
 *                           the answer makes the day count, or all must. On
 *                           streaks / logging_rate the answer is the counted
 *                           one; on single_feature_stats it is the yes label.
 *                           Always persisted)
 *   project_narrative     → (none)
 *
 * Motion vocab is the fixed CLAUDE.md set: surface pop-in uses the
 * { stiffness:500, damping:30 } spring; the submit button uses
 * whileHover scale 1.1 / whileTap scale 0.9; the close button adds rotate 90;
 * picker chips enter/exit with opacity 0→1, scale 0.6→1, transformOrigin
 * 'left center' wrapped in AnimatePresence.
 */

export type EditorSurface = 'feature' | 'project' | 'dashboard'

// Phase 1 kinds only — the CRUD accepts Phase 2 placeholders but they have no
// builder yet, so the editor doesn't offer them.
const FEATURE_KINDS: { value: SummaryKind; label: string }[] = [
  { value: 'single_feature_stats', label: 'Single-feature stats' },
  { value: 'stratified_single', label: 'Stratified (by key / paired feature)' },
  { value: 'streaks', label: 'Streaks / consistency' },
  { value: 'logging_rate', label: 'Logging rate (% of periods)' },
  { value: 'period_over_period', label: 'Period over period' },
  { value: 'outliers', label: 'Outliers (top / bottom)' },
]
// Kinds available when the source is a project tag (a tag has no value, so it
// resolves to a per-day check-in-count series — see backend Stage 15). Excludes
// stratified_single (needs a feature value) and offers count-appropriate labels.
const PROJECT_TAG_KINDS: { value: SummaryKind; label: string }[] = [
  { value: 'streaks', label: 'Streaks / consistency' },
  { value: 'logging_rate', label: 'Logging rate (% of periods)' },
  { value: 'single_feature_stats', label: 'Check-in count stats' },
  { value: 'period_over_period', label: 'Period over period (check-in count)' },
  { value: 'outliers', label: 'Busiest / quietest days' },
  { value: 'project_narrative', label: 'Project narrative (Insights)' },
]

// Precomputed-narrative tier is fixed to month. The three-tier machinery
// (week/month/year) is backend refresh-cadence infrastructure; the only surface
// that reads a precomputed tier is the project Insights card, which reads month.
// New configs pin to this; an edited config keeps whatever tiers it already has.
const DEFAULT_TIERS: SummaryTier[] = ['month']

const GAP_FILLS: SummaryGapFill[] = ['none', 'zero', 'carry_forward', 'interpolate']
const GAP_FILL_LABEL: Record<SummaryGapFill, string> = {
  none: 'None',
  zero: 'Zero',
  carry_forward: 'Carry forward',
  interpolate: 'Interpolate',
}

/** Kinds that take a top_n param. */
const TOP_N_KINDS = new Set<SummaryKind>(['single_feature_stats', 'outliers'])

// Yes/no source settings, mirroring `bool_feature.COUNT_KINDS` / `DAY_RULE_KINDS`
// on the backend: `count` on the kinds that count days, `day_rule` on every
// kind that reads the answer per day.
const COUNT_KINDS = new Set<SummaryKind>(['streaks', 'logging_rate'])
const DAY_RULE_KINDS = new Set<SummaryKind>(['streaks', 'logging_rate', 'single_feature_stats'])
const DAY_RULE_OPTIONS: { value: BoolDayRule; label: string }[] = [
  { value: 'any', label: 'Any checkin' },
  { value: 'all', label: 'Every checkin' },
]

const SURFACE_SPRING = { type: 'spring', stiffness: 500, damping: 30 } as const
const CHIP_SPRING = { type: 'spring', stiffness: 500, damping: 30 } as const

interface SummaryConfigEditorProps {
  isOpen: boolean
  onClose: () => void
  /** Called after a successful create/update with the persisted config. */
  onSaved: (config: SummaryConfig) => void
  /** Which surface the editor is mounted on (drives default source + pin). */
  surface: EditorSurface
  /** The current feature id (feature surface) — the default source feature. */
  featureId?: number
  /** The current project id (project surface) — the default source project. */
  projectId?: number
  /** Sibling features (for the source picker on a feature surface and the
   *  stratification-key picker). Optional; the editor still works without it. */
  features?: Feature[]
  /** Selectable projects for the dashboard surface's project-tag source picker. */
  projects?: Project[]
  /** When set, the editor edits this config instead of creating a new one. */
  existing?: SummaryConfig | null
}

/** Group features by their project for the source pickers, so the dropdown is
 *  scoped by project (an <optgroup> per project) rather than one flat list of
 *  every feature. Project-less features (e.g. system features) fall in "Other",
 *  which sorts last; named projects sort alphabetically. */
function groupFeaturesByProject(
  feats: Feature[],
  projects: Project[],
): { key: string; label: string; features: Feature[] }[] {
  const byKey = new Map<string, { key: string; label: string; features: Feature[] }>()
  for (const f of feats) {
    const key = f.project_id != null ? String(f.project_id) : 'none'
    if (!byKey.has(key)) {
      const label =
        f.project?.title ??
        projects.find((p) => p.id === f.project_id)?.title ??
        (f.project_id != null ? `Project ${f.project_id}` : 'Other')
      byKey.set(key, { key, label, features: [] })
    }
    byKey.get(key)!.features.push(f)
  }
  const groups = Array.from(byKey.values())
  groups.forEach((g) => g.features.sort((a, b) => a.title.localeCompare(b.title)))
  return groups.sort((a, b) =>
    a.key === 'none' ? 1 : b.key === 'none' ? -1 : a.label.localeCompare(b.label),
  )
}

const SummaryConfigEditor: React.FC<SummaryConfigEditorProps> = ({
  isOpen,
  onClose,
  onSaved,
  surface,
  featureId,
  projectId,
  features = [],
  projects = [],
  existing = null,
}) => {
  // The source type is chosen in the editor (a Feature/Project-tag toggle),
  // not dictated by the page. Its default follows the opening context — a
  // feature page opens on 'feature', the dashboard/project page on 'project' —
  // but the user can switch either way.
  type SourceType = 'feature' | 'project'
  const [sourceType, setSourceType] = useState<SourceType>(
    featureId != null ? 'feature' : 'project',
  )
  const kindOptions = sourceType === 'project' ? PROJECT_TAG_KINDS : FEATURE_KINDS
  const defaultKind = kindOptions[0].value

  // The editor self-loads the full feature and project lists on open so either
  // source type is pickable from any page. Props seed them (no flash) but the
  // fetched lists win. Projects load with ALL statuses — both to offer archived
  // ones as a source and because a feature's group heading needs its project's
  // title (the feature list endpoint carries only project_id, not the title).
  // A host may hand in an active-only `projects` prop (the dashboard does); the
  // fetch replaces it, so the archived ones appear a beat later rather than not
  // at all.
  const [featureOptions, setFeatureOptions] = useState<Feature[]>(features)
  const [projectOptions, setProjectOptions] = useState<Project[]>(projects)
  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    getFeatures(FeatureStatus.ACTIVE)
      .then((fs) => { if (!cancelled) setFeatureOptions(fs) })
      .catch((e) => console.error('Failed to load features for summary editor', e))
    getProjects()
      .then((ps) => { if (!cancelled) setProjectOptions(ps) })
      .catch((e) => console.error('Failed to load projects for summary editor', e))
    return () => { cancelled = true }
  }, [isOpen])

  // Also surface this summary on the dashboard. Implicit (and hidden) when the
  // editor is opened FROM the dashboard; an opt-in checkbox otherwise.
  const [showOnDashboard, setShowOnDashboard] = useState(false)

  const [kind, setKind] = useState<SummaryKind>(defaultKind)
  const [userTitle, setUserTitle] = useState('')
  const [userDescription, setUserDescription] = useState('')
  const [gapFill, setGapFill] = useState<SummaryGapFill>('none')
  const [topN, setTopN] = useState<number>(5)
  const [stratFeatureId, setStratFeatureId] = useState<number | ''>('')
  // How same-day check-ins combine into one daily point on the comparison
  // chart. Defaults to summing; the user can flip to averaging.
  const [sameDayAgg, setSameDayAgg] = useState<'sum' | 'mean'>('sum')
  // Which direction the chart paints green (progress). Defaults to higher-is-
  // better; 'none' = no good/bad (neutral fill).
  // TODO(goals): directionality is a goals concept, not a summary one. When the
  // Goals feature lands (see future_work/goals.md), remove progress_direction
  // from summary configs and source "which way is better" from the goal instead.
  const [progressDir, setProgressDir] = useState<'higher' | 'lower' | 'none'>('higher')
  // Streaks cadence: which bucket a streak counts consecutive runs in. 'daily'
  // is the original calendar-day behavior; 'weekly'/'monthly' keep the streak
  // alive with at least one check-in per week / month.
  const [cadence, setCadence] = useState<'daily' | 'weekly' | 'monthly'>('daily')
  // Yes/no source only: which answer's days a streak / logging rate counts.
  const [count, setCount] = useState<'yes' | 'no'>('yes')
  // Yes/no source only: how a day with several answers is decided.
  const [dayRule, setDayRule] = useState<BoolDayRule>('any')
  const [sourceFeatureId, setSourceFeatureId] = useState<number | ''>(
    featureId ?? '',
  )
  // Dashboard surface: which project tag this summary sources.
  const [sourceProjectId, setSourceProjectId] = useState<number | ''>(
    projectId ?? '',
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Seed the form from `existing` (edit mode) or surface defaults (create).
  useEffect(() => {
    if (!isOpen) return
    if (existing) {
      setSourceType(existing.scope === 'project' ? 'project' : 'feature')
      setShowOnDashboard(!!existing.pin_surfaces?.dashboard)
      setKind(existing.kind)
      setUserTitle(existing.user_title ?? '')
      setUserDescription(existing.user_description ?? '')
      setGapFill(existing.gap_fill_policy ?? 'none')
      setTopN(Number(existing.params?.top_n ?? 5))
      setStratFeatureId(
        existing.params?.strat_feature_id != null
          ? Number(existing.params.strat_feature_id)
          : '',
      )
      setSameDayAgg(existing.params?.same_day_agg === 'mean' ? 'mean' : 'sum')
      setCadence(
        existing.params?.cadence === 'weekly'
          ? 'weekly'
          : existing.params?.cadence === 'monthly'
            ? 'monthly'
            : 'daily',
      )
      setCount(existing.params?.count === 'no' ? 'no' : 'yes')
      setDayRule(existing.params?.day_rule === 'all' ? 'all' : 'any')
      setProgressDir(
        existing.params?.progress_direction === 'lower'
          ? 'lower'
          : existing.params?.progress_direction === 'none'
            ? 'none'
            : 'higher',
      )
      setSourceFeatureId(
        existing.source_feature_ids?.length
          ? existing.source_feature_ids[0]
          : featureId ?? '',
      )
      setSourceProjectId(
        existing.source_project_ids?.length
          ? existing.source_project_ids[0]
          : projectId ?? '',
      )
    } else {
      setSourceType(featureId != null ? 'feature' : 'project')
      setShowOnDashboard(false)
      setKind(defaultKind)
      setUserTitle('')
      setUserDescription('')
      setGapFill('none')
      setTopN(5)
      setStratFeatureId('')
      setSameDayAgg('sum')
      setProgressDir('higher')
      setCadence('daily')
      setCount('yes')
      setSourceFeatureId(featureId ?? '')
      setSourceProjectId(projectId ?? '')
    }
    setError(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, existing, surface, featureId, projectId])

  // Every project is offered as a tag source, active or not, split into two
  // groups so the live ones come first. Put-away projects are summarized for the
  // same reason they're kept: the check-ins are still there and a finished
  // project is exactly the thing you want a retrospective on. Nothing downstream
  // cares about status — the builders and aggregations filter by check-in
  // membership only — so the picker was the whole of the restriction: an
  // archived project's summary couldn't be created, and an existing one couldn't
  // be edited (its source wasn't in the list, so the editor opened on "Select a
  // project…" and refused to save).
  const projectGroups = useMemo(() => {
    const active = projectOptions.filter((p) => p.status === ProjectStatus.ACTIVE)
    const inactive = projectOptions.filter((p) => p.status !== ProjectStatus.ACTIVE)
    return [
      { key: 'active', label: 'Active', projects: active },
      { key: 'inactive', label: 'Archived / completed', projects: inactive },
    ].filter((g) => g.projects.length > 0)
  }, [projectOptions])
  // Any feature can be a source, whatever its project's status — same reasoning
  // as the tag source above. `groupFeaturesByProject` already labels each group
  // with its project's title, so an archived project's features stay findable
  // under their own heading.
  const sourceableFeatures = featureOptions
  const stratCandidates = useMemo(
    () => sourceableFeatures.filter((f) => f.id !== sourceFeatureId),
    [sourceableFeatures, sourceFeatureId],
  )
  // Feature pickers grouped by project (see groupFeaturesByProject).
  const featureGroups = useMemo(
    () => groupFeaturesByProject(sourceableFeatures, projectOptions),
    [sourceableFeatures, projectOptions],
  )
  const stratGroups = useMemo(
    () => groupFeaturesByProject(stratCandidates, projectOptions),
    [stratCandidates, projectOptions],
  )

  // A yes/no source feature's labels, which name the `count` and `day_rule`
  // choices. Null for any other source.
  const boolSourceLabels = useMemo(() => {
    if (sourceType !== 'feature' || sourceFeatureId === '') return null
    const source =
      featureOptions.find((f) => f.id === sourceFeatureId) ??
      features.find((f) => f.id === sourceFeatureId)
    return isBoolSub(source?.config) ? boolLabels(source?.config?.params) : null
  }, [sourceType, sourceFeatureId, featureOptions, features])
  const countLabels = COUNT_KINDS.has(kind) ? boolSourceLabels : null
  const dayRuleLabels = DAY_RULE_KINDS.has(kind) ? boolSourceLabels : null
  const countLabel = countLabels ? countLabels[count === 'yes' ? 0 : 1] : null

  // When the source type flips, drop a kind the new type doesn't support
  // (e.g. project_narrative on a feature source, stratified_single on a tag).
  useEffect(() => {
    if (!kindOptions.some((k) => k.value === kind)) setKind(kindOptions[0].value)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceType])

  const buildParams = (): Record<string, any> | null => {
    const params: Record<string, any> = {}
    if (TOP_N_KINDS.has(kind)) params.top_n = topN
    if (kind === 'stratified_single' && stratFeatureId !== '') {
      params.strat_feature_id = stratFeatureId
    }
    if (kind === 'period_over_period') {
      // Persist only the non-default choices so configs stay terse.
      if (sameDayAgg === 'mean') params.same_day_agg = 'mean'
      if (progressDir !== 'higher') params.progress_direction = progressDir
    }
    // Streaks and logging_rate default to daily; only persist a coarser cadence.
    if ((kind === 'streaks' || kind === 'logging_rate') && cadence !== 'daily') {
      params.cadence = cadence
    }
    if (countLabels) params.count = count
    if (dayRuleLabels) params.day_rule = dayRule
    return Object.keys(params).length ? params : null
  }

  const handleSubmit = async () => {
    setError(null)
    if (sourceType === 'feature' && sourceFeatureId === '') {
      setError('Select a source feature.')
      return
    }
    if (sourceType === 'project' && sourceProjectId === '') {
      setError('Select a project.')
      return
    }

    // Source drives scope. A summary shows on its source's feature/project page
    // via source membership (the pages load by source, not by pin), so the only
    // meaningful pin is the dashboard opt-in — implicit when opened from the
    // dashboard, else the "show on dashboard" checkbox.
    const pinToDashboard = surface === 'dashboard' || showOnDashboard

    const body: SummaryConfigInput = {
      kind,
      scope: sourceType,
      source_feature_ids:
        sourceType === 'feature' && sourceFeatureId !== '' ? [Number(sourceFeatureId)] : [],
      source_project_ids:
        sourceType === 'project' && sourceProjectId !== '' ? [Number(sourceProjectId)] : [],
      user_title: userTitle.trim() || null,
      user_description: userDescription.trim() || null,
      // Keep an edited config's existing tiers; new configs pin to month.
      tiers: existing?.tiers?.length ? existing.tiers : DEFAULT_TIERS,
      gap_fill_policy: gapFill,
      params: buildParams(),
      pin_surfaces: pinToDashboard ? { dashboard: true } : null,
    }

    setSaving(true)
    try {
      const saved = existing
        ? await updateSummaryConfig(existing.id, body)
        : await createSummaryConfig(body)
      onSaved(saved)
      onClose()
    } catch (e) {
      console.error('Failed to save summary config', e)
      setError('Could not save this summary. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onClick={(e) => {
            if (e.target === e.currentTarget && !saving) onClose()
          }}
          data-testid="summary-config-editor-backdrop"
        >
          <motion.div
            className="flex max-h-[90dvh] w-full max-w-lg flex-col rounded-lg border border-gray-200 bg-white shadow-2xl"
            initial={{ opacity: 0, scale: 0.95, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -8 }}
            transition={SURFACE_SPRING}
            style={{ transformOrigin: 'top center' }}
            data-testid="summary-config-editor"
          >
            {/* Header and footer stay put; only the fields scroll. On a phone the
                form runs past the fold, and with Save scrolled out of view the
                ✕ looked like the way to apply — it discards. */}
            <div className="flex shrink-0 items-center justify-between px-6 pb-4 pt-6">
              <h3 className="text-lg font-semibold text-gray-900">
                {existing ? 'Edit summary' : 'Add summary'}
              </h3>
              <motion.button
                type="button"
                aria-label="Close"
                onClick={onClose}
                disabled={saving}
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </motion.button>
            </div>

            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 pb-4">
              {/* Source: a Feature or a Project tag. Drives which kinds show. */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Source</label>
                <div className="mb-2 inline-flex rounded-md border border-gray-300 p-0.5 text-sm">
                  {(['feature', 'project'] as const).map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setSourceType(t)}
                      aria-pressed={sourceType === t}
                      aria-label={t === 'feature' ? 'Feature source' : 'Project tag source'}
                      className={`rounded px-3 py-1 transition-colors ${
                        sourceType === t
                          ? 'bg-action text-plum'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {t === 'feature' ? 'Feature' : 'Project tag'}
                    </button>
                  ))}
                </div>
                {sourceType === 'feature' ? (
                  <select
                    aria-label="Source feature"
                    value={sourceFeatureId === '' ? '' : String(sourceFeatureId)}
                    onChange={(e) =>
                      setSourceFeatureId(e.target.value === '' ? '' : Number(e.target.value))
                    }
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  >
                    <option value="">Select a feature…</option>
                    {featureGroups.map((g) => (
                      <optgroup key={g.key} label={g.label}>
                        {g.features.map((f) => (
                          <option key={f.id} value={f.id}>{f.title}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                ) : (
                  <>
                    <select
                      aria-label="Source project"
                      value={sourceProjectId === '' ? '' : String(sourceProjectId)}
                      onChange={(e) =>
                        setSourceProjectId(e.target.value === '' ? '' : Number(e.target.value))
                      }
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    >
                      <option value="">Select a project…</option>
                      {projectGroups.map((g) => (
                        <optgroup key={g.key} label={g.label}>
                          {g.projects.map((p) => (
                            <option key={p.id} value={p.id}>{p.title}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-400">
                      Summarizes the check-ins tagged with this project.
                    </p>
                  </>
                )}
              </div>

              {/* Kind */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Kind</label>
                <select
                  aria-label="Summary kind"
                  value={kind}
                  onChange={(e) => setKind(e.target.value as SummaryKind)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  {kindOptions.map((k) => (
                    <option key={k.value} value={k.value}>{k.label}</option>
                  ))}
                </select>
              </div>

              {/* Title + description */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Title</label>
                <input
                  type="text"
                  aria-label="Title"
                  value={userTitle}
                  maxLength={255}
                  onChange={(e) => setUserTitle(e.target.value)}
                  placeholder="Optional display title"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  aria-label="Description"
                  value={userDescription}
                  onChange={(e) => setUserDescription(e.target.value)}
                  rows={2}
                  placeholder="Optional"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              </div>

              {/* Gap fill */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Gap fill</label>
                <select
                  aria-label="Gap fill policy"
                  value={gapFill}
                  onChange={(e) => setGapFill(e.target.value as SummaryGapFill)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  {GAP_FILLS.map((g) => (
                    <option key={g} value={g}>{GAP_FILL_LABEL[g]}</option>
                  ))}
                </select>
              </div>

              {/* Kind-conditional params row */}
              <div data-testid="params-row">
                {/* Top-N categories are meaningless for a tag count series. */}
                {TOP_N_KINDS.has(kind) &&
                  !(sourceType === 'project' && kind === 'single_feature_stats') && (
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Top N</label>
                    <input
                      type="number"
                      aria-label="Top N"
                      min={1}
                      max={50}
                      value={topN}
                      onChange={(e) => setTopN(Number(e.target.value))}
                      className="w-28 rounded-md border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>
                )}

                {(kind === 'streaks' || kind === 'logging_rate') && (
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      {kind === 'streaks'
                        ? 'How often do checkins need to appear in order to form a streak?'
                        : 'Over which period should the % of logged checkins be counted?'}
                    </label>
                    <select
                      aria-label={kind === 'streaks' ? 'Streak cadence' : 'Logging rate cadence'}
                      value={cadence}
                      onChange={(e) =>
                        setCadence(e.target.value as 'daily' | 'weekly' | 'monthly')
                      }
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-400">
                      {countLabel ? (
                        kind === 'streaks' ? (
                          <>
                            Stays alive with at least one “{countLabel}” a{' '}
                            {cadence === 'daily' ? 'day' : cadence === 'weekly' ? 'week' : 'month'}.
                          </>
                        ) : (
                          <>
                            Shows the share of{' '}
                            {cadence === 'daily' ? 'days' : cadence === 'weekly' ? 'weeks' : 'months'}{' '}
                            with a “{countLabel}”.
                          </>
                        )
                      ) : kind === 'streaks' ? (
                        <>
                          The streak stays alive as long as you check in at least once a{' '}
                          {cadence === 'daily' ? 'day' : cadence === 'weekly' ? 'week' : 'month'}.
                        </>
                      ) : (
                        <>
                          Shows the share of{' '}
                          {cadence === 'daily' ? 'days' : cadence === 'weekly' ? 'weeks' : 'months'}{' '}
                          in the period on which you logged at least once.
                        </>
                      )}
                    </p>
                  </div>
                )}

                {dayRuleLabels && (
                  <div className="mt-4 flex flex-wrap gap-x-6 gap-y-4">
                    {countLabels && (
                      <div>
                        <span className="mb-1 block text-sm font-medium text-gray-700">Count</span>
                        <div
                          role="group"
                          aria-label="Count"
                          className="inline-flex rounded-md border border-gray-300 p-0.5 text-sm"
                        >
                          {(['yes', 'no'] as const).map((c, i) => (
                            <button
                              key={c}
                              type="button"
                              onClick={() => setCount(c)}
                              aria-pressed={count === c}
                              className={`rounded px-3 py-1 transition-colors ${
                                count === c
                                  ? 'bg-action text-plum'
                                  : 'text-gray-600 hover:bg-gray-100'
                              }`}
                            >
                              {countLabels[i]}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    <div>
                      <span
                        id="summary-day-rule-label"
                        className="mb-1 block text-sm font-medium text-gray-700"
                      >
                        {countLabel
                          ? `A day counts as “${countLabel}” when`
                          : `A day is “${dayRuleLabels[0]}” when`}
                      </span>
                      <div
                        role="group"
                        aria-labelledby="summary-day-rule-label"
                        className="inline-flex rounded-md border border-gray-300 p-0.5 text-sm"
                      >
                        {DAY_RULE_OPTIONS.map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setDayRule(opt.value)}
                            aria-pressed={dayRule === opt.value}
                            className={`rounded px-3 py-1 transition-colors ${
                              dayRule === opt.value
                                ? 'bg-action text-plum'
                                : 'text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {kind === 'stratified_single' && (
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Stratification key (paired feature)
                    </label>
                    <select
                      aria-label="Stratification key"
                      value={stratFeatureId === '' ? '' : String(stratFeatureId)}
                      onChange={(e) =>
                        setStratFeatureId(e.target.value === '' ? '' : Number(e.target.value))
                      }
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    >
                      <option value="">By map key</option>
                      {stratGroups.map((g) => (
                        <optgroup key={g.key} label={g.label}>
                          {g.features.map((f) => (
                            <option key={f.id} value={f.id}>{f.title}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                )}

                {kind === 'period_over_period' && (
                  <div className="space-y-4">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Same-day check-ins
                      </label>
                      <select
                        aria-label="Same-day check-ins"
                        value={sameDayAgg}
                        onChange={(e) => setSameDayAgg(e.target.value as 'sum' | 'mean')}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                      >
                        <option value="sum">Sum</option>
                        <option value="mean">Average</option>
                      </select>
                      <p className="mt-1 text-xs text-gray-400">
                        How multiple check-ins on the same day combine into one point on the chart.
                      </p>
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Which direction is better?
                      </label>
                      <select
                        aria-label="Which direction is better?"
                        value={progressDir}
                        onChange={(e) =>
                          setProgressDir(e.target.value as 'higher' | 'lower' | 'none')
                        }
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                      >
                        <option value="higher">Higher</option>
                        <option value="lower">Lower</option>
                        <option value="none">Neither</option>
                      </select>
                      <p className="mt-1 text-xs text-gray-400">
                        The chart fills green for the better direction, red for worse.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Dashboard pin — implicit when the editor is opened from the
                  dashboard, an opt-in otherwise. A summary always shows on its
                  source's feature/project page via source membership. */}
              {surface !== 'dashboard' && (
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    aria-label="Show on dashboard"
                    checked={showOnDashboard}
                    onChange={(e) => setShowOnDashboard(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  Also show on dashboard
                </label>
              )}

              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.6 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.6 }}
                    transition={CHIP_SPRING}
                    style={{ transformOrigin: 'left center' }}
                    className="text-sm text-rose-600"
                  >
                    {error}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="flex shrink-0 justify-end gap-3 border-t border-gray-100 px-6 py-4">
              <motion.button
                type="button"
                onClick={onClose}
                disabled={saving}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                className="rounded-md bg-secondary px-4 py-2 text-sm text-gray-700 hover:bg-secondary-dark disabled:opacity-50"
              >
                Cancel
              </motion.button>
              <motion.button
                type="button"
                onClick={handleSubmit}
                disabled={saving}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                className="rounded-md bg-action px-4 py-2 text-sm font-medium text-plum hover:bg-action-dark disabled:opacity-50"
                data-testid="summary-config-submit"
              >
                {saving ? 'Saving…' : existing ? 'Save' : 'Create'}
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default SummaryConfigEditor
